import type { PDFPageProxy } from "pdfjs-dist";
import type { PdfRect } from "./pdfBearbeitung";

/**
 * Ausschnitt einer Seite als PNG aufnehmen (Teilplan 06 B, „Ausschneiden &
 * Verschieben“). Gerendert wird NUR der Seiteninhalt — ohne Annotationen, die
 * der Editor als eigene Ebene zeigt und die beim Speichern ohnehin in der Datei
 * stehen — in hoher Auflösung (Standard 3×), mit PDF.js selbst.
 *
 * Aufgenommen wird immer UNGEDREHT, also in Userspace-Richtung: der Server
 * zeichnet das Bild mit pdf-lib in den Userspace der Zielseite. Deshalb kennt
 * `ausschnittViewport` keine Drehung (Abweichung vom Plan, der eine
 * `rotation` vorsah — eine gedrehte Aufnahme käme auf dem Server verdreht an).
 */

/** Obergrenze fürs Canvas — hält das PNG in aller Regel unter den 5 MB des Servers. */
export const MAX_PIXEL = 4_000_000;
/** Harte Grenze des Servers je Bild — darüber wird mit kleinerer Skala neu aufgenommen. */
const MAX_BYTES = 5 * 1024 * 1024 - 64 * 1024;

export interface AusschnittViewport {
    /** Canvas-Größe in Pixeln. */
    breite: number;
    hoehe: number;
    /** Tatsächliche Skala (ggf. unter der gewünschten). */
    skala: number;
    /** Immer 0 — siehe Modulkommentar. */
    rotation: 0;
    /** Zusatz-Transform für `page.render`: schiebt die linke obere Ecke des Rechtecks auf (0|0). */
    transform: [number, number, number, number, number, number];
}

/**
 * Rechnung für `page.render` mit `page.getViewport({ scale: skala, rotation: 0 })`:
 * PDF.js bildet (x|y) auf (skala·(x − view₀) | skala·(view₃ − y)) ab. Die
 * linke obere Ecke des Rechtecks ist (rect₀|rect₃) — dorthin verschiebt der
 * Zusatz-Transform den Ursprung des Canvas.
 */
export function ausschnittViewport(rect: PdfRect, skala: number, view: readonly number[]): AusschnittViewport {
    const w = Math.max(rect[2] - rect[0], 0);
    const h = Math.max(rect[3] - rect[1], 0);
    const flaeche = Math.max(w * h, 1e-6);
    const s = Math.min(skala, Math.floor(Math.sqrt(MAX_PIXEL / flaeche) * 100) / 100);
    return {
        breite: Math.max(1, Math.ceil(w * s)),
        hoehe: Math.max(1, Math.ceil(h * s)),
        skala: s,
        rotation: 0,
        transform: [1, 0, 0, 1, -s * (rect[0] - view[0]), -s * (view[3] - rect[3])],
    };
}

/** Ab dieser Helligkeit (dunkelster Kanal) ist ein Pixel ganz durchsichtig … */
const WEISS_AB = 250;
/** … bis hierhin voll deckend, dazwischen linear. */
const DECKEND_BIS = 240;

/**
 * Weiß durchsichtig machen — sonst deckte das verschobene Stück den Untergrund
 * an der Zielstelle mit einem weißen Kasten ab.
 *
 * Weich statt harter Schwelle (Review 28.09.2026): Die hellen Kantenpixel der
 * Kantenglättung werden teilweise durchsichtig, verschobener Text wirkt dann
 * nicht fetter. Sehr helle, aber nicht weiße Flächen (#F0F0F0 als
 * Zeilenhintergrund) bleiben voll deckend.
 */
export function weissFreistellen(px: Uint8ClampedArray): void {
    for (let i = 0; i < px.length; i += 4) {
        const hell = Math.min(px[i], px[i + 1], px[i + 2]);
        if (hell >= WEISS_AB) px[i + 3] = 0;
        else if (hell > DECKEND_BIS) px[i + 3] = Math.round((px[i + 3] * (WEISS_AB - hell)) / (WEISS_AB - DECKEND_BIS));
    }
}

/**
 * Userspace-Rechtecke, die in der Aufnahme leer bleiben sollen, als Canvas-Pixel
 * `[x, y, breite, hoehe]` — auf den Ausschnitt beschnitten, außerhalb entfallen.
 * Abbildung wie in `ausschnittViewport`: x → s·(x − rect₀), y → s·(rect₃ − y).
 */
export function loeschRechteckeImCanvas(
    rect: PdfRect,
    vp: Pick<AusschnittViewport, "skala" | "breite" | "hoehe">,
    loeschen: readonly PdfRect[],
): [number, number, number, number][] {
    const s = vp.skala;
    const ergebnis: [number, number, number, number][] = [];
    for (const l of loeschen) {
        const x0 = Math.max(0, Math.floor(s * (l[0] - rect[0])));
        const x1 = Math.min(vp.breite, Math.ceil(s * (l[2] - rect[0])));
        const y0 = Math.max(0, Math.floor(s * (rect[3] - l[3])));
        const y1 = Math.min(vp.hoehe, Math.ceil(s * (rect[3] - l[1])));
        if (x1 > x0 && y1 > y0) ergebnis.push([x0, y0, x1 - x0, y1 - y0]);
    }
    return ergebnis;
}

const alsPng = (canvas: HTMLCanvasElement): Promise<Blob> =>
    new Promise((resolve, reject) =>
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Der Ausschnitt konnte nicht aufgenommen werden."))), "image/png"),
    );

/**
 * Nimmt `rect` (Userspace) der Seite als PNG mit durchsichtigem Weiß auf.
 * `loeschen`: Bereiche, die leer bleiben — was schon zum Entfernen markiert ist
 * (auch Quellen anderer Ausschnitte), darf im Bild nicht wieder auftauchen.
 */
export async function nimmAuf(
    page: Pick<PDFPageProxy, "view" | "render" | "getViewport">,
    rect: PdfRect,
    loeschen: readonly PdfRect[] = [],
    skala = 3,
): Promise<Blob> {
    // Dynamisch: der Viewer hat PDF.js längst geladen, und Tests, die dieses
    // Modul nur für die reinen Funktionen laden, brauchen kein PDF.js (DOMMatrix).
    const { AnnotationMode } = await import("pdfjs-dist");
    let versuch = skala;
    for (let i = 0; i < 4; i++) {
        const vp = ausschnittViewport(rect, versuch, page.view);
        const canvas = document.createElement("canvas");
        canvas.width = vp.breite;
        canvas.height = vp.hoehe;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) throw new Error("Der Ausschnitt konnte nicht aufgenommen werden.");
        await page.render({
            canvas,
            canvasContext: ctx,
            viewport: page.getViewport({ scale: vp.skala, rotation: 0 }),
            transform: vp.transform,
            annotationMode: AnnotationMode.DISABLE,
            background: "rgb(255,255,255)",
        }).promise;
        const bild = ctx.getImageData(0, 0, vp.breite, vp.hoehe);
        weissFreistellen(bild.data);
        ctx.putImageData(bild, 0, 0);
        for (const [x, y, w, h] of loeschRechteckeImCanvas(rect, vp, loeschen)) ctx.clearRect(x, y, w, h);
        const blob = await alsPng(canvas);
        if (blob.size <= MAX_BYTES) return blob;
        versuch = vp.skala * 0.7;
    }
    throw new Error("Der Ausschnitt ist zu groß — bitte einen kleineren Bereich wählen.");
}
